import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { doc, deleteDoc, updateDoc, writeBatch, collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { getAuth, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db, storage } from '../firebaseConfig';
import { useSkipsData } from '../context/SkipsDataContext';
import { useResponsive } from '../hooks/useResponsive';
import { deleteImageFromStorage } from '../utils/storageUtils';
import { exportToCSV, exportToPDF } from '../utils/exportUtils';
import { geocodeAddress } from '../utils/geocodingUtils';
import { matchesSearchTerm } from '../utils/searchUtils';
import toast from 'react-hot-toast';
import ViewToggle from './ViewToggle';
import FilterSidebar from './FilterSidebar';
import RecordsTable from './RecordsTable';
import DeleteModal from './DeleteModal';
import EditModal from './EditModal';
import PasswordConfirmModal from './PasswordConfirmModal';
import JobTicket from './JobTicket';

// Custom debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const RecordsPage = () => {
    const navigate = useNavigate();
    const viewport = useResponsive();
    const {
        companyId,
        companyName,
        getFilteredJobsForRecords,
        isLoading,
        userIsAdmin
    } = useSkipsData();

    const auth = getAuth();
    const [drivers, setDrivers] = useState([]);

    // Fetch drivers for the company (for assignment dropdown)
    useEffect(() => {
        const fetchDrivers = async () => {
            if (!companyId) return;

            try {
                const usersRef = collection(db, 'users');
                const q = query(
                    usersRef,
                    where('companyId', '==', companyId)
                );
                const snapshot = await getDocs(q);

                const driversList = [];
                snapshot.forEach((doc) => {
                    const data = doc.data();
                    driversList.push({
                        id: doc.id,
                        name: data.name || data.email?.split('@')[0] || 'Unknown',
                        email: data.email,
                        isAdmin: data.admin === true
                    });
                });

                driversList.sort((a, b) => a.name.localeCompare(b.name));
                setDrivers(driversList);
            } catch (error) {
                console.error('Error fetching drivers:', error);
            }
        };

        fetchDrivers();
    }, [companyId]);

    // Redirect mobile users to map page
    useEffect(() => {
        if (viewport.isMobile) {
            toast.error('Records page is only available on desktop. Redirecting to map view...', {
                duration: 4000
            });
            navigate('/map');
        }
    }, [viewport.isMobile, navigate]);

    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [enlargedImage, setEnlargedImage] = useState(null);
    const [selectedRecords, setSelectedRecords] = useState(new Set());
    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [focusJobId, setFocusJobId] = useState(null);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);

    // Ref to track if we've already processed the focusRowId
    const processedFocusRef = useRef(null);
    const exportMenuRef = useRef(null);

    // Reset the processed focus ref when component mounts
    useEffect(() => {
        return () => {
            processedFocusRef.current = null;
        };
    }, []);

    // Handle click outside to close export dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
                setExportMenuOpen(false);
            }
        };

        if (exportMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [exportMenuOpen]);

    // Delete modal state
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        type: null, // 'single', 'bulk'
        recordInfo: null,
        isProcessing: false,
        isDeletingProgress: false // New: true when showing progress bar
    });

    // Cancellation flag for bulk delete
    const cancelDeletionRef = useRef(false);

    // Password confirmation modal state
    const [passwordConfirmModal, setPasswordConfirmModal] = useState({
        isOpen: false,
        type: null, // 'single', 'bulk'
        recordInfo: null,
        isProcessing: false
    });

    // Edit modal state
    const [editModal, setEditModal] = useState({
        isOpen: false,
        job: null,
        mode: 'edit', // 'edit' or 'add'
        isProcessing: false
    });

    // Bulk status update modal state
    const [bulkStatusModal, setBulkStatusModal] = useState({
        isOpen: false,
        selectedStatus: '',
        isProcessing: false
    });

    const filteredJobs = getFilteredJobsForRecords();

    // Debounce search term
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // Apply search filtering with smart postcode matching
    const searchedJobs = useMemo(() => {
        if (!debouncedSearchTerm.trim()) {
            return filteredJobs;
        }

        return filteredJobs.filter(job => matchesSearchTerm(job, debouncedSearchTerm));
    }, [filteredJobs, debouncedSearchTerm]);

    // Handle View on Map navigation
    const handleViewOnMap = (job) => {
        // Validate coordinates - check position array
        if (!job.position || !Array.isArray(job.position) || job.position.length !== 2) {
            toast.error('This job has no location coordinates');
            return;
        }

        const [lat, lng] = job.position;

        // Validate that coordinates are valid numbers
        if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
            toast.error('Invalid coordinates for this job');
            return;
        }

        // Store navigation data
        sessionStorage.setItem('focusJobId', job.id);
        sessionStorage.setItem('mapCenter', JSON.stringify({
            lat: lat,
            lng: lng,
            zoom: 16
        }));

        // Navigate
        toast.success('Opening map view...');
        navigate('/map');
    };

    // Handle focus when coming from map
    useEffect(() => {
        const focusRowId = sessionStorage.getItem('focusRowId');

        // Skip if no focus ID or we've already processed it
        if (!focusRowId || processedFocusRef.current === focusRowId) {
            return;
        }

        // Skip if jobs haven't loaded yet
        if (searchedJobs.length === 0) {
            return;
        }

        // Mark as processed to prevent duplicate runs
        processedFocusRef.current = focusRowId;

        // Check if job exists in filtered jobs
        const jobExists = searchedJobs.some(job => job.id === focusRowId);

        if (!jobExists) {
            toast.error('Job not found in current filter. Try clearing filters.');
            sessionStorage.removeItem('focusRowId');
            setFocusJobId(null);
            return;
        }

        // Set focusJobId for table to navigate to correct page
        setFocusJobId(focusRowId);

        // Select the job (check it)
        setSelectedRecords(new Set([focusRowId]));

        // Scroll to row and highlight - increased timeout for DOM rendering and page navigation
        setTimeout(() => {
            const rowElement = document.querySelector(`[data-job-id="${focusRowId}"]`);
            if (rowElement) {
                rowElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });

                // Highlight row
                setHighlightedRowId(focusRowId);
                setTimeout(() => {
                    setHighlightedRowId(null);
                    setFocusJobId(null); // Clear after highlighting
                }, 3000);

                toast.success('Job found in records');
            } else {
                toast.error('Job row not found. It may be on a different page.');
            }
            sessionStorage.removeItem('focusRowId');
        }, 800);
    }, [searchedJobs]);

    // Reauthenticate user with password
    const reauthenticateUser = async (password) => {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user || !user.email) {
            throw new Error('No authenticated user found');
        }

        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
    };

    // Single delete
    const handleDeleteClick = (job) => {
        // Client-side validation: Ensure job belongs to user's company
        if (job.companyId !== companyId) {
            toast.error('Unauthorized: This record belongs to another company');
            console.error('Security violation: Attempt to delete job from different company', {
                jobCompanyId: job.companyId,
                userCompanyId: companyId
            });
            return;
        }

        // Show password confirmation first
        setPasswordConfirmModal({
            isOpen: true,
            type: 'single',
            recordInfo: {
                address: job.formattedAddress || job.streetAddress || 'N/A',
                skipId: job.skipId,
                imageUrl: job.imageUrl,
                jobId: job.id
            },
            isProcessing: false
        });
    };

    const handleSingleDelete = async () => {
        setDeleteModal(prev => ({ ...prev, isProcessing: true }));

        try {
            const { jobId, imageUrl } = deleteModal.recordInfo;

            // Delete image from storage
            if (imageUrl) {
                await deleteImageFromStorage(imageUrl);
            }

            // Delete Firestore document
            await deleteDoc(doc(db, 'jobs', jobId));

            toast.success('Record deleted successfully');
            setDeleteModal({ isOpen: false, type: null, recordInfo: null, isProcessing: false });
        } catch (error) {
            console.error('Error deleting record:', error);
            toast.error('Failed to delete record. Please try again.');
            setDeleteModal(prev => ({ ...prev, isProcessing: false }));
        }
    };

    // Bulk delete
    const handleBulkDeleteClick = () => {
        const selectedJobs = searchedJobs.filter(job => selectedRecords.has(job.id));

        // Client-side validation: Ensure all jobs belong to user's company
        const unauthorizedJobs = selectedJobs.filter(job => job.companyId !== companyId);
        if (unauthorizedJobs.length > 0) {
            toast.error('Unauthorized: Some selected records belong to another company');
            console.error('Security violation: Attempt to delete jobs from different company', {
                unauthorizedCount: unauthorizedJobs.length,
                userCompanyId: companyId,
                unauthorizedJobs: unauthorizedJobs.map(j => ({ id: j.id, companyId: j.companyId }))
            });
            return;
        }

        // Show password confirmation first
        setPasswordConfirmModal({
            isOpen: true,
            type: 'bulk',
            recordInfo: {
                count: selectedJobs.length,
                jobs: selectedJobs
            },
            isProcessing: false
        });
    };

    const handleBulkDelete = async () => {
        const { jobs } = deleteModal.recordInfo;
        const total = jobs.length;

        // Reset cancellation flag and show progress
        cancelDeletionRef.current = false;
        setDeleteModal(prev => ({
            ...prev,
            isProcessing: true,
            isDeletingProgress: true,
            recordInfo: {
                ...prev.recordInfo,
                progress: 0,
                total: total
            }
        }));

        // Give React time to render the progress bar before starting deletions
        await new Promise(resolve => setTimeout(resolve, 100));

        let deletedCount = 0;
        const errors = [];

        try {
            // Delete records one by one to show progress and allow cancellation
            for (let i = 0; i < jobs.length; i++) {
                // Check if user cancelled
                if (cancelDeletionRef.current) {
                    toast.error(`Deletion cancelled. ${deletedCount} of ${total} records were deleted.`);
                    break;
                }

                const job = jobs[i];

                try {
                    // Delete image from storage
                    if (job.imageUrl) {
                        await deleteImageFromStorage(job.imageUrl);
                    }

                    // Delete Firestore document
                    await deleteDoc(doc(db, 'jobs', job.id));

                    deletedCount++;

                    // Update progress (using functional form to avoid eslint warning)
                    const currentProgress = deletedCount;
                    setDeleteModal(prev => ({
                        ...prev,
                        recordInfo: {
                            ...prev.recordInfo,
                            progress: currentProgress,
                            total: total
                        }
                    }));

                    // Small delay to make progress visible and give UI time to update
                    await new Promise(resolve => setTimeout(resolve, 50));
                } catch (error) {
                    console.error(`Error deleting record ${job.id}:`, error);
                    errors.push(job.id);
                }
            }

            // Show completion message
            if (!cancelDeletionRef.current) {
                if (errors.length > 0) {
                    toast.error(`${deletedCount} records deleted. ${errors.length} failed.`);
                } else {
                    toast.success(`${deletedCount} record${deletedCount !== 1 ? 's' : ''} deleted successfully`);
                }
            }

            // Clear selection and close modal
            setSelectedRecords(new Set());
            setDeleteModal({
                isOpen: false,
                type: null,
                recordInfo: null,
                isProcessing: false,
                isDeletingProgress: false
            });
        } catch (error) {
            console.error('Error during bulk deletion:', error);
            toast.error('Failed to delete records. Please try again.');
            setDeleteModal(prev => ({
                ...prev,
                isProcessing: false,
                isDeletingProgress: false
            }));
        }
    };

    // Handle cancellation of bulk delete
    const handleCancelDeletion = () => {
        cancelDeletionRef.current = true;
    };


    // Edit
    const handleEditClick = (job) => {
        // Client-side validation: Ensure job belongs to user's company
        if (job.companyId !== companyId) {
            toast.error('Unauthorized: This record belongs to another company');
            console.error('Security violation: Attempt to edit job from different company', {
                jobCompanyId: job.companyId,
                userCompanyId: companyId
            });
            return;
        }

        setEditModal({
            isOpen: true,
            job: job,
            mode: 'edit',
            isProcessing: false
        });
    };

    // Add new record
    const handleAddRecordClick = () => {
        setEditModal({
            isOpen: true,
            job: null,
            mode: 'add',
            isProcessing: false
        });
    };

    const handleEditSave = async (formData) => {
        setEditModal(prev => ({ ...prev, isProcessing: true }));

        try {
            if (editModal.mode === 'add') {
                // Add new record
                const auth = getAuth();
                const currentUser = auth.currentUser;

                if (!currentUser) {
                    toast.error('User not authenticated');
                    setEditModal(prev => ({ ...prev, isProcessing: false }));
                    return;
                }

                // Generate unique skip ID
                const skipId = `skip_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
                const currentDate = new Date();
                const formattedDate = currentDate.toISOString();

                // Geocode the address to get coordinates
                const geocodingToast = toast.loading('Geocoding address...');
                const geocodeResult = await geocodeAddress(formData.formattedAddress);

                let latitude = 0;
                let longitude = 0;

                if (geocodeResult) {
                    latitude = geocodeResult.lat;
                    longitude = geocodeResult.lng;
                    toast.success('Address geocoded successfully', { id: geocodingToast });
                } else {
                    toast.error('Could not geocode address. Record will be saved without map coordinates.', { id: geocodingToast });
                }

                // Prepare new job data
                const newJobData = {
                    skipId: skipId,
                    companyId: companyId,
                    userId: currentUser.uid,
                    size: formData.size,
                    wasteType: formData.wasteType,
                    jobType: formData.jobType,
                    status: formData.status,
                    driverNote: formData.driverNote || '',
                    paymentStatus: formData.paymentStatus || 'Unknown',
                    invoiced: formData.invoiced === true,
                    address: {
                        formattedAddress: formData.formattedAddress,
                        streetAddress: formData.streetAddress,
                        displayAddress: formData.streetAddress,
                        postcode: formData.postcode || null,
                        businessName: formData.businessName || null
                    },
                    postcode: formData.postcode || null,  // Also at top level for search
                    businessName: formData.businessName || null,  // Also at top level for search
                    timestamp: formattedDate,
                    formattedDate: formattedDate,
                    latitude: latitude,
                    longitude: longitude
                };

                // Handle image upload for new record
                if (formData.newImage) {
                    const timestamp = Date.now();
                    const imageName = `${skipId}_${timestamp}.jpg`;
                    const imageRef = ref(storage, `images/${imageName}`);

                    await uploadBytes(imageRef, formData.newImage);
                    const imageUrl = await getDownloadURL(imageRef);
                    newJobData.imageUrl = imageUrl;
                }

                // Add document to Firestore
                await addDoc(collection(db, 'jobs'), newJobData);

                toast.success('Record added successfully');
                setEditModal({ isOpen: false, job: null, mode: 'edit', isProcessing: false });
            } else {
                // Edit existing record
                const job = editModal.job;

                // Prepare the update data
                const updateData = {
                    size: formData.size,
                    wasteType: formData.wasteType,
                    jobType: formData.jobType,
                    status: formData.status,
                    driverNote: formData.driverNote || '',
                    paymentStatus: formData.paymentStatus || 'Unknown',
                    invoiced: formData.invoiced === true
                };

                // Check if address has changed
                const oldAddress = job.formattedAddress || job.streetAddress || '';
                const addressChanged = formData.formattedAddress !== oldAddress;

                // If address changed, geocode the new address
                if (addressChanged) {
                    const geocodingToast = toast.loading('Geocoding updated address...');
                    const geocodeResult = await geocodeAddress(formData.formattedAddress);

                    if (geocodeResult) {
                        updateData.latitude = geocodeResult.lat;
                        updateData.longitude = geocodeResult.lng;
                        toast.success('Address geocoded successfully', { id: geocodingToast });
                    } else {
                        toast.error('Could not geocode new address. Coordinates unchanged.', { id: geocodingToast });
                    }
                }

                // Handle address update
                updateData.address = {
                    ...(job.address && typeof job.address === 'object' ? job.address : {}),
                    formattedAddress: formData.formattedAddress,
                    streetAddress: formData.streetAddress,
                    displayAddress: formData.streetAddress,
                    postcode: formData.postcode || null,
                    businessName: formData.businessName || null
                };
                // Also update top-level fields for search
                updateData.postcode = formData.postcode || null;
                updateData.businessName = formData.businessName || null;

                // Handle image replacement
                if (formData.newImage) {
                    // Delete old image
                    if (job.imageUrl) {
                        await deleteImageFromStorage(job.imageUrl);
                    }

                    // Upload new image
                    const timestamp = Date.now();
                    const imageName = `${job.skipId}_${timestamp}.jpg`;
                    const imageRef = ref(storage, `images/${imageName}`);

                    await uploadBytes(imageRef, formData.newImage);
                    const newImageUrl = await getDownloadURL(imageRef);

                    updateData.imageUrl = newImageUrl;
                }

                // Update Firestore document
                const docRef = doc(db, 'jobs', job.id);
                await updateDoc(docRef, updateData);

                toast.success('Record updated successfully');
                setEditModal({ isOpen: false, job: null, mode: 'edit', isProcessing: false });
            }
        } catch (error) {
            console.error('Error saving record:', error.code || error.message);
            toast.error(`Failed to save record: ${error.message}`);
            setEditModal(prev => ({ ...prev, isProcessing: false }));
        }
    };

    // Bulk status update
    const handleBulkStatusClick = () => {
        setBulkStatusModal({
            isOpen: true,
            selectedStatus: '',
            isProcessing: false
        });
    };

    const handleBulkStatusUpdate = async () => {
        if (!bulkStatusModal.selectedStatus) {
            toast.error('Please select a status');
            return;
        }

        const selectedJobs = searchedJobs.filter(job => selectedRecords.has(job.id));

        if (selectedJobs.length === 0) {
            toast.error('No jobs selected');
            return;
        }

        // Confirmation
        const confirmed = window.confirm(
            `Update status to "${bulkStatusModal.selectedStatus}" for ${selectedJobs.length} job${selectedJobs.length !== 1 ? 's' : ''}?`
        );

        if (!confirmed) return;

        setBulkStatusModal(prev => ({ ...prev, isProcessing: true }));

        try {
            // Firestore batch update (max 500 operations per batch)
            const batchSize = 500;
            const progressToast = toast.loading(`Updating ${selectedJobs.length} job${selectedJobs.length !== 1 ? 's' : ''}...`);

            for (let i = 0; i < selectedJobs.length; i += batchSize) {
                const batch = writeBatch(db);
                const batchJobs = selectedJobs.slice(i, i + batchSize);

                batchJobs.forEach(job => {
                    const docRef = doc(db, 'jobs', job.id);
                    batch.update(docRef, { status: bulkStatusModal.selectedStatus });
                });

                await batch.commit();

                // Update progress
                if (selectedJobs.length > batchSize) {
                    toast.loading(`Updated ${Math.min(i + batchSize, selectedJobs.length)} of ${selectedJobs.length} jobs...`, {
                        id: progressToast
                    });
                }
            }

            toast.success(`Updated ${selectedJobs.length} job${selectedJobs.length !== 1 ? 's' : ''} to ${bulkStatusModal.selectedStatus}`, {
                id: progressToast
            });

            // Clear selection and close modal
            setSelectedRecords(new Set());
            setBulkStatusModal({ isOpen: false, selectedStatus: '', isProcessing: false });
        } catch (error) {
            console.error('Error updating job statuses:', error);
            toast.error('Failed to update job statuses. Please try again.');
            setBulkStatusModal(prev => ({ ...prev, isProcessing: false }));
        }
    };

    const handleWasteTypeUpdate = async (jobId, newWasteType) => {
        const toastId = toast.loading('Updating waste type...');

        try {
            const docRef = doc(db, 'jobs', jobId);
            await updateDoc(docRef, { wasteType: newWasteType });
            toast.success('Waste type updated successfully', { id: toastId });
        } catch (error) {
            console.error('Error updating waste type:', error);
            toast.error('Failed to update waste type. Please try again.', { id: toastId });
        }
    };

    const handleSizeUpdate = async (jobId, newSize) => {
        const toastId = toast.loading('Updating size...');

        try {
            const docRef = doc(db, 'jobs', jobId);
            await updateDoc(docRef, { size: newSize });
            toast.success('Size updated successfully', { id: toastId });
        } catch (error) {
            console.error('Error updating size:', error);
            toast.error('Failed to update size. Please try again.', { id: toastId });
        }
    };

    const handleStatusUpdate = async (jobId, newStatus) => {
        const toastId = toast.loading('Updating status...');

        try {
            const docRef = doc(db, 'jobs', jobId);
            await updateDoc(docRef, { status: newStatus });
            toast.success('Status updated successfully', { id: toastId });
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status. Please try again.', { id: toastId });
        }
    };

    const handlePaymentStatusUpdate = async (jobId, newStatus) => {
        const toastId = toast.loading('Updating payment status...');

        try {
            const docRef = doc(db, 'jobs', jobId);
            await updateDoc(docRef, { paymentStatus: newStatus });
            toast.success('Payment status updated successfully', { id: toastId });
        } catch (error) {
            console.error('Error updating payment status:', error);
            toast.error('Failed to update payment status. Please try again.', { id: toastId });
        }
    };

    const handleDriverNoteUpdate = async (jobId, newNote) => {
        const toastId = toast.loading('Updating driver note...');

        try {
            const docRef = doc(db, 'jobs', jobId);
            await updateDoc(docRef, { driverNote: newNote });
            toast.success('Driver note updated successfully', { id: toastId });
        } catch (error) {
            console.error('Error updating driver note:', error);
            toast.error('Failed to update driver note. Please try again.', { id: toastId });
        }
    };

    // Handle driver assignment
    const handleAssignmentChange = async (jobId, driverId, driverName) => {
        const toastId = toast.loading(driverId ? 'Assigning driver...' : 'Removing assignment...');

        try {
            const docRef = doc(db, 'jobs', jobId);
            await updateDoc(docRef, {
                assignedDriverId: driverId || null,
                assignedDriverName: driverName || null,
                assignedAt: driverId ? serverTimestamp() : null,
                assignedBy: driverId ? auth.currentUser?.uid : null
            });

            if (driverId) {
                toast.success(`Assigned to ${driverName}`, { id: toastId });
            } else {
                toast.success('Assignment removed', { id: toastId });
            }
        } catch (error) {
            console.error('Error assigning driver:', error);
            toast.error('Failed to assign driver. Please try again.', { id: toastId });
        }
    };

    // Export functions
    const handleExportCSV = () => {
        try {
            exportToCSV(searchedJobs, companyName);
            toast.success('CSV exported successfully');
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('Failed to export CSV');
        }
    };

    const handleExportPDF = () => {
        try {
            exportToPDF(searchedJobs, companyName);
            toast.success('PDF exported successfully');
        } catch (error) {
            console.error('Error exporting PDF:', error);
            toast.error('Failed to export PDF');
        }
    };

    // Print functions
    const generatePrintHTML = (jobs) => {
        // Generate ticket HTML for each job
        const ticketsHTML = jobs.map(job =>
            ReactDOMServer.renderToStaticMarkup(<JobTicket job={job} companyName={companyName} />)
        ).join('');

        // Generate filename for the document title
        const generateFilename = () => {
            if (jobs.length === 1) {
                const job = jobs[0];
                const address = job.formattedAddress || job.streetAddress || 'Unknown_Address';
                const date = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
                // Clean address for filename (remove special chars, replace spaces with underscores)
                const cleanAddress = address.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').substring(0, 50);
                return `JobTicket_${cleanAddress}_${date}`;
            } else {
                const date = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
                return `JobTickets_${jobs.length}_tickets_${date}`;
            }
        };

        const documentTitle = generateFilename();

        // CSS for the print window
        const cssContent = `
            /* Common styles for both screen and print */
            .job-ticket {
                width: 148mm;
                height: 210mm;
                max-height: 210mm;
                padding: 8mm;
                margin: 0 auto;
                background: white;
                color: #000;
                font-family: Arial, Helvetica, sans-serif;
                font-size: 10pt;
                line-height: 1.3;
                box-sizing: border-box;
                overflow: hidden;
            }
            .ticket-header {
                text-align: center;
                border-bottom: 3px solid #000;
                padding-bottom: 6px;
                margin-bottom: 10px;
            }
            .company-name {
                font-size: 20pt;
                font-weight: bold;
                margin: 0 0 4px 0;
                text-transform: uppercase;
                letter-spacing: 1px;
            }
            .ticket-type {
                font-size: 10pt;
                font-weight: bold;
                letter-spacing: 2px;
            }
            .section-label {
                font-size: 9pt;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 6px;
                border-bottom: 2px solid #000;
                padding-bottom: 2px;
            }
            .collection-address-section {
                margin-bottom: 12px;
                padding: 8px;
                border: 2px solid #000;
            }
            .collection-address {
                font-size: 11pt;
                font-weight: bold;
                line-height: 1.4;
                margin-top: 6px;
                max-height: 40px;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .job-details-section {
                margin-bottom: 12px;
                padding: 8px;
                border: 2px solid #000;
            }
            .details-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 6px;
                margin-top: 6px;
            }
            .detail-row {
                display: flex;
                justify-content: space-between;
                padding: 3px 0;
                border-bottom: 1px solid #ccc;
            }
            .detail-label {
                font-weight: bold;
                font-size: 9pt;
            }
            .detail-value {
                font-size: 9pt;
                text-align: right;
            }
            .detail-highlight {
                font-weight: bold;
                text-decoration: underline;
            }
            .detail-status {
                text-transform: uppercase;
                font-weight: bold;
            }
            .payment-to-pay {
                font-weight: bold;
                color: #F44336 !important;
                text-decoration: underline;
                background-color: #FFEBEE;
                padding: 2px 6px;
                border-radius: 3px;
            }
            .customer-notes-section {
                margin-bottom: 10px;
                padding: 8px;
                border: 2px solid #000;
            }
            .customer-notes {
                margin-top: 6px;
                padding: 6px;
                background: #f9f9f9;
                border: 1px dashed #666;
                min-height: 30px;
                max-height: 60px;
                font-size: 9pt;
                line-height: 1.3;
                white-space: pre-wrap;
                overflow: hidden;
                word-wrap: break-word;
            }
            .signature-section {
                margin-bottom: 10px;
                padding: 8px;
                border: 2px solid #000;
            }
            .signature-box {
                margin-top: 8px;
            }
            .signature-line {
                display: flex;
                align-items: center;
                margin-bottom: 10px;
            }
            .signature-label {
                font-size: 9pt;
                font-weight: bold;
                min-width: 75px;
                margin-right: 8px;
            }
            .signature-line-border {
                flex: 1;
                border-bottom: 2px solid #000;
                height: 20px;
            }
            .signature-line-short {
                max-width: 140px;
            }
            .ticket-footer-qr {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding-top: 8px;
                border-top: 2px solid #000;
                margin-top: 8px;
            }
            .qr-section {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 4px;
                padding: 5px;
            }
            .qr-label {
                font-size: 8pt;
                font-weight: bold;
                text-align: center;
                margin-top: 2px;
            }
            .footer-text {
                flex: 1;
                text-align: right;
                font-size: 8pt;
                color: #666;
            }
            .footer-text .reference {
                font-family: monospace;
                margin-bottom: 4px;
                font-size: 8pt;
            }
            .footer-text .print-info {
                font-size: 8pt;
                font-style: italic;
            }
            @media print {
                @page {
                    size: A5 portrait;
                    margin: 10mm;
                }
                body * {
                    visibility: hidden;
                }
                .job-ticket,
                .job-ticket * {
                    visibility: visible;
                }
                .job-ticket {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 148mm;
                    height: 210mm;
                    max-height: 210mm;
                    margin: 0;
                    padding: 8mm;
                    page-break-after: always;
                    overflow: hidden;
                }
                .job-ticket:last-child {
                    page-break-after: auto;
                }
                .collection-address-section {
                    background: white;
                }
                .customer-notes {
                    background: white;
                }
                .collection-address-section,
                .job-details-section,
                .customer-notes-section,
                .signature-section {
                    border-color: #000;
                    page-break-inside: avoid;
                }
                * {
                    color: #000 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                .ticket-footer-qr {
                    border-top-color: #000 !important;
                }
                .footer-text {
                    color: #666 !important;
                }
                .qr-label {
                    color: #000 !important;
                }
            }
            @media screen {
                .job-ticket {
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
                    margin: 20px auto;
                    border: 1px solid #ddd;
                }
            }
        `;

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>${documentTitle}</title>
                <style>${cssContent}</style>
            </head>
            <body>
                ${ticketsHTML}
            </body>
            </html>
        `;
    };

    const handlePrintTicket = (job) => {
        try {
            const printWindow = window.open('', '_blank');

            if (!printWindow) {
                toast.error('Please allow popups to print tickets');
                return;
            }

            const html = generatePrintHTML([job]);
            printWindow.document.write(html);
            printWindow.document.close();

            // Wait for content to load before printing
            printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
            };

            // Close window after printing (or if user cancels)
            printWindow.onafterprint = () => {
                printWindow.close();
            };

        } catch (error) {
            console.error('Error printing ticket:', error);
            toast.error('Failed to print ticket');
        }
    };

    const handlePrintSelectedTickets = () => {
        const selectedJobs = searchedJobs.filter(job => selectedRecords.has(job.id));

        if (selectedJobs.length === 0) {
            toast.error('No jobs selected');
            return;
        }

        // Confirmation for large batches
        if (selectedJobs.length > 20) {
            const confirmed = window.confirm(
                `You are about to print ${selectedJobs.length} tickets. This may take a while. Continue?`
            );
            if (!confirmed) return;
        }

        try {
            const printWindow = window.open('', '_blank');

            if (!printWindow) {
                toast.error('Please allow popups to print tickets');
                return;
            }

            const loadingToast = toast.loading(`Preparing ${selectedJobs.length} tickets for printing...`);

            const html = generatePrintHTML(selectedJobs);
            printWindow.document.write(html);
            printWindow.document.close();

            printWindow.onload = () => {
                toast.success(`${selectedJobs.length} tickets ready to print`, { id: loadingToast });
                printWindow.focus();
                printWindow.print();
            };

            printWindow.onafterprint = () => {
                printWindow.close();
            };

        } catch (error) {
            console.error('Error printing tickets:', error);
            toast.error('Failed to print tickets');
        }
    };

    // Handle password confirmation
    const handlePasswordConfirm = async (password) => {
        setPasswordConfirmModal(prev => ({ ...prev, isProcessing: true }));

        try {
            // Reauthenticate user
            await reauthenticateUser(password);

            // Password is correct, close password modal and open delete confirmation modal
            const { type, recordInfo } = passwordConfirmModal;
            setPasswordConfirmModal({ isOpen: false, type: null, recordInfo: null, isProcessing: false });

            setDeleteModal({
                isOpen: true,
                type: type,
                recordInfo: recordInfo,
                isProcessing: false
            });

            toast.success('Password verified');
        } catch (error) {
            console.error('Reauthentication error:', error);

            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                toast.error('Incorrect password. Please try again.');
            } else if (error.code === 'auth/too-many-requests') {
                toast.error('Too many failed attempts. Please try again later.');
            } else {
                toast.error('Authentication failed. Please try again.');
            }

            setPasswordConfirmModal(prev => ({ ...prev, isProcessing: false }));
        }
    };

    // Handle delete based on type
    const handleDeleteConfirm = () => {
        switch (deleteModal.type) {
            case 'single':
                handleSingleDelete();
                break;
            case 'bulk':
                handleBulkDelete();
                break;
            default:
                break;
        }
    };

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <div style={{
                    fontSize: '20px',
                    color: '#3B82F6'
                }}>
                    Loading records for {companyName || companyId}...
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw' }}>
            {/* Fixed Header with View Toggle */}
            <ViewToggle />

            {/* Main Content Area */}
            <div style={{ display: 'flex', flex: 1, marginTop: '50px', position: 'relative' }}>
                {/* Filter Sidebar */}
                <FilterSidebar view="records" isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

                {/* Records Content */}
                <div style={{
                    flex: 1,
                    marginLeft: '340px', // Optimal clearance: sidebar (300px) + shadow + generous visual spacing
                    transition: 'margin-left 0.3s ease-in-out',
                    padding: '20px',
                    backgroundColor: '#F9FAFB',
                    overflow: 'auto'
                }}>
                    {/* Page Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px',
                        flexWrap: 'wrap',
                        gap: '12px'
                    }}>
                        <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
                            Skip Records
                        </h1>

                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {selectedRecords.size > 0 && (
                                <>
                                    <button
                                        onClick={handleBulkStatusClick}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: 'white',
                                            color: '#44403C',
                                            border: '1px solid #D6D3D1',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Update Status ({selectedRecords.size})
                                    </button>
                                    <button
                                        onClick={handlePrintSelectedTickets}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: 'white',
                                            color: '#44403C',
                                            border: '1px solid #D6D3D1',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Print Selected ({selectedRecords.size})
                                    </button>
                                    <button
                                        onClick={handleBulkDeleteClick}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#DC2626',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Delete Selected ({selectedRecords.size})
                                    </button>
                                </>
                            )}
                            <button
                                onClick={handleAddRecordClick}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#166534',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                + Add Record
                            </button>

                            {/* Export dropdown */}
                            <div ref={exportMenuRef} style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setExportMenuOpen(!exportMenuOpen)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#166534',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    Export
                                    <span style={{ fontSize: '10px' }}>▼</span>
                                </button>

                                {exportMenuOpen && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        marginTop: '4px',
                                        backgroundColor: 'white',
                                        border: '1px solid #D1D5DB',
                                        borderRadius: '4px',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                        zIndex: 1000,
                                        minWidth: '150px'
                                    }}>
                                        <button
                                            onClick={() => {
                                                handleExportCSV();
                                                setExportMenuOpen(false);
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                backgroundColor: 'white',
                                                border: 'none',
                                                borderBottom: '1px solid #F3F4F6',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                textAlign: 'left',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                        >
                                            📊 Export CSV
                                        </button>
                                        <button
                                            onClick={() => {
                                                handleExportPDF();
                                                setExportMenuOpen(false);
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                backgroundColor: 'white',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                textAlign: 'left',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                        >
                                            📄 Export PDF
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div style={{
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{
                            flex: 1,
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <span style={{
                                position: 'absolute',
                                left: '12px',
                                fontSize: '18px',
                                color: '#6B7280'
                            }}>
                                🔍
                            </span>
                            <input
                                type="text"
                                placeholder="Search by address, postcode, business name, skip ID, size, waste type..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 40px 12px 44px',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    backgroundColor: 'white'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
                                onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    title="Clear search"
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        width: '24px',
                                        height: '24px',
                                        border: 'none',
                                        borderRadius: '50%',
                                        backgroundColor: '#E5E7EB',
                                        color: '#6B7280',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        padding: 0
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#D1D5DB'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                        <div style={{
                            padding: '12px 16px',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: '#374151',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                        }}>
                            {searchedJobs.length} of {filteredJobs.length} jobs
                        </div>
                    </div>

                    {/* Records Table */}
                    <RecordsTable
                        jobs={searchedJobs}
                        onDelete={handleDeleteClick}
                        onEdit={handleEditClick}
                        onViewImage={setEnlargedImage}
                        onPrint={handlePrintTicket}
                        onViewOnMap={handleViewOnMap}
                        selectedRecords={selectedRecords}
                        onSelectionChange={setSelectedRecords}
                        highlightedRowId={highlightedRowId}
                        focusJobId={focusJobId}
                        onPaymentStatusChange={handlePaymentStatusUpdate}
                        onStatusChange={handleStatusUpdate}
                        onSizeChange={handleSizeUpdate}
                        onWasteTypeChange={handleWasteTypeUpdate}
                        onDriverNoteChange={handleDriverNoteUpdate}
                        drivers={drivers}
                        onAssignmentChange={handleAssignmentChange}
                        userIsAdmin={userIsAdmin}
                    />
                </div>
            </div>

            {/* Delete Modal */}
            <DeleteModal
                type={deleteModal.type}
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, type: null, recordInfo: null, isProcessing: false, isDeletingProgress: false })}
                onConfirm={handleDeleteConfirm}
                onCancel={handleCancelDeletion}
                recordInfo={deleteModal.recordInfo}
                isProcessing={deleteModal.isProcessing}
                isDeletingProgress={deleteModal.isDeletingProgress}
            />

            {/* Edit Modal */}
            <EditModal
                isOpen={editModal.isOpen}
                onClose={() => setEditModal({ isOpen: false, job: null, mode: 'edit', isProcessing: false })}
                onSave={handleEditSave}
                job={editModal.job}
                mode={editModal.mode}
                isProcessing={editModal.isProcessing}
            />

            {/* Password Confirmation Modal */}
            <PasswordConfirmModal
                isOpen={passwordConfirmModal.isOpen}
                onClose={() => setPasswordConfirmModal({ isOpen: false, type: null, recordInfo: null, isProcessing: false })}
                onConfirm={handlePasswordConfirm}
                isProcessing={passwordConfirmModal.isProcessing}
                message={
                    passwordConfirmModal.type === 'single'
                        ? 'You are about to permanently delete a record. This action cannot be undone. Please enter your password to confirm.'
                        : passwordConfirmModal.type === 'bulk'
                        ? `You are about to permanently delete ${passwordConfirmModal.recordInfo?.count || 0} records. This action cannot be undone. Please enter your password to confirm.`
                        : 'This action requires password confirmation.'
                }
            />

            {/* Bulk Status Update Modal */}
            {bulkStatusModal.isOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '24px',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#111827',
                            marginBottom: '16px'
                        }}>
                            Update Status for {selectedRecords.size} Job{selectedRecords.size !== 1 ? 's' : ''}
                        </h2>

                        <div style={{
                            marginBottom: '24px'
                        }}>
                            <p style={{
                                fontSize: '14px',
                                color: '#6B7280',
                                marginBottom: '16px'
                            }}>
                                Select the new status for the selected jobs:
                            </p>

                            {['delivered', 'collected'].map((status) => (
                                <label
                                    key={status}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '12px',
                                        marginBottom: '8px',
                                        border: '2px solid',
                                        borderColor: bulkStatusModal.selectedStatus === status ? '#3B82F6' : '#E5E7EB',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        backgroundColor: bulkStatusModal.selectedStatus === status ? '#EFF6FF' : 'white',
                                        transition: 'all 0.15s'
                                    }}
                                    onMouseOver={(e) => {
                                        if (bulkStatusModal.selectedStatus !== status) {
                                            e.currentTarget.style.backgroundColor = '#F9FAFB';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (bulkStatusModal.selectedStatus !== status) {
                                            e.currentTarget.style.backgroundColor = 'white';
                                        }
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="status"
                                        value={status}
                                        checked={bulkStatusModal.selectedStatus === status}
                                        onChange={(e) => setBulkStatusModal(prev => ({ ...prev, selectedStatus: e.target.value }))}
                                        style={{
                                            marginRight: '12px',
                                            width: '18px',
                                            height: '18px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                    <span style={{
                                        fontSize: '15px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        textTransform: 'capitalize'
                                    }}>
                                        {status}
                                    </span>
                                </label>
                            ))}
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={() => setBulkStatusModal({ isOpen: false, selectedStatus: '', isProcessing: false })}
                                disabled={bulkStatusModal.isProcessing}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    border: '1px solid #D1D5DB',
                                    borderRadius: '6px',
                                    cursor: bulkStatusModal.isProcessing ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    opacity: bulkStatusModal.isProcessing ? 0.5 : 1
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBulkStatusUpdate}
                                disabled={bulkStatusModal.isProcessing || !bulkStatusModal.selectedStatus}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: bulkStatusModal.isProcessing || !bulkStatusModal.selectedStatus ? '#93C5FD' : '#3B82F6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: bulkStatusModal.isProcessing || !bulkStatusModal.selectedStatus ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                {bulkStatusModal.isProcessing ? 'Updating...' : 'Update Status'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Enlarged Image Modal */}
            {enlargedImage && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        cursor: 'pointer',
                        padding: '20px'
                    }}
                    onClick={() => setEnlargedImage(null)}
                >
                    <div
                        style={{
                            maxWidth: '85%',
                            maxHeight: '85%',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={enlargedImage}
                            alt="Enlarged view"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                            }}
                        />
                        <button
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                color: 'black',
                                border: 'none',
                                fontSize: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                            }}
                            onClick={() => setEnlargedImage(null)}
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecordsPage;
